// DESCRIPTION: Event list page — handles data fetching, pagination, filtering, sorting, and search.
// DESCRIPTION: Search is debounced (useDebounce); filtering and sorting are applied server-side.

import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import EventListTable from './EventListTable';
import EventFilters from './EventFilters';
import { useDebounce } from '../utils/useDebounce';

const DEFAULT_STATUS = 'publish,draft';
const DEFAULT_TYPE = '';
const DEFAULT_SORT = { orderby: 'date', order: 'ASC' };

const EventList = ( { onCreateNew, onEditEvent } ) => {
	const [ events, setEvents ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ notices, setNotices ] = useState( [] );

	// Pagination state
	const [ currentPage, setCurrentPage ] = useState( 1 );
	const [ itemsPerPage, setItemsPerPage ] = useState( 10 );
	const [ totalItems, setTotalItems ] = useState( 0 );
	const [ totalPages, setTotalPages ] = useState( 1 );

	// Filter + sort state
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ status, setStatus ] = useState( DEFAULT_STATUS );
	const [ eventType, setEventType ] = useState( DEFAULT_TYPE );
	const [ sort, setSort ] = useState( DEFAULT_SORT );

	// Debounce the search input so live search fires once per pause, not per keystroke.
	const searchQuery = useDebounce( searchInput, 400 );

	const hasActiveFilters =
		searchInput !== '' ||
		status !== DEFAULT_STATUS ||
		eventType !== DEFAULT_TYPE;

	/**
	 * Load events with pagination, filtering, sorting, and search.
	 *
	 * @since PBOOKING_SINCE
	 *
	 * @param {number} [page]    Page number to load.
	 * @param {number} [perPage] Items per page.
	 */
	const loadEvents = async ( page = currentPage, perPage = itemsPerPage ) => {
		try {
			setIsLoading( true );

			const params = new URLSearchParams();
			params.set( 'page', page );
			params.set( 'per_page', perPage );
			if ( status ) {
				params.set( 'status', status );
			}
			if ( searchQuery ) {
				params.set( 'search', searchQuery );
			}
			if ( eventType ) {
				params.set( 'event_type', eventType );
			}
			if ( sort.orderby ) {
				params.set( 'orderby', sort.orderby );
			}
			if ( sort.order ) {
				params.set( 'order', sort.order );
			}

			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events?${ params.toString() }`,
			} );

			// Handle new response format from updated Events API
			if ( response.success && response.result && response.pagination ) {
				setEvents(
					Array.isArray( response.result ) ? response.result : []
				);
				setTotalItems( response.pagination.total_items || 0 );
				setTotalPages( response.pagination.total_pages || 1 );
				setCurrentPage( response.pagination.current_page || page );
				setNotices( [] );
			} else if ( response.success === false ) {
				throw new Error(
					response.message ||
						__( 'Failed to load events.', 'nemtly-booking' )
				);
			} else {
				setEvents( [] );
				setTotalItems( 0 );
				setTotalPages( 1 );
				setNotices( [
					{
						type: 'warning',
						message: __(
							'Unexpected response format from server.',
							'nemtly-booking'
						),
					},
				] );
			}
		} catch ( error ) {
			setNotices( [
				{
					type: 'error',
					message:
						error.message ||
						__( 'Failed to load events.', 'nemtly-booking' ),
				},
			] );
			setEvents( [] );
			setTotalItems( 0 );
			setTotalPages( 1 );
		} finally {
			setIsLoading( false );
		}
	};

	useEffect( () => {
		loadEvents( currentPage, itemsPerPage );
	}, [
		currentPage,
		itemsPerPage,
		searchQuery,
		status,
		eventType,
		sort.orderby,
		sort.order,
	] );

	/**
	 * Handle page change
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} page New page number.
	 */
	const handlePageChange = ( page ) => {
		setCurrentPage( page );
	};

	/**
	 * Handle items per page change
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} perPage New items per page.
	 */
	const handleItemsPerPageChange = ( perPage ) => {
		setItemsPerPage( perPage );
		setCurrentPage( 1 );
	};

	/**
	 * Handle live search input. Resets to page 1 (no-op when already there).
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} value New search string.
	 */
	const handleSearchChange = ( value ) => {
		setSearchInput( value );
		if ( currentPage !== 1 ) {
			setCurrentPage( 1 );
		}
	};

	/**
	 * Handle status filter change. Resets to page 1.
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} value New status value.
	 */
	const handleStatusChange = ( value ) => {
		setStatus( value );
		setCurrentPage( 1 );
	};

	/**
	 * Handle event type filter change. Resets to page 1.
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} value New event type value.
	 */
	const handleEventTypeChange = ( value ) => {
		setEventType( value );
		setCurrentPage( 1 );
	};

	/**
	 * Handle sortable column header click.
	 *
	 * @since PBOOKING_SINCE
	 * @param {string} orderby   Sort key to activate.
	 * @param {string} direction Sort direction ('ASC' | 'DESC').
	 */
	const handleSortChange = ( orderby, direction ) => {
		setSort( { orderby, order: direction } );
		setCurrentPage( 1 );
	};

	/**
	 * Clear all filters and reset sort to the default.
	 *
	 * @since PBOOKING_SINCE
	 */
	const handleClearFilters = () => {
		setSearchInput( '' );
		setStatus( DEFAULT_STATUS );
		setEventType( DEFAULT_TYPE );
		setSort( DEFAULT_SORT );
		setCurrentPage( 1 );
	};

	/**
	 * Handle event deletion
	 *
	 * @since PBOOKING_SINCE
	 * @param {number} eventId The event ID to delete.
	 */
	const handleDeleteEvent = async ( eventId ) => {
		if (
			! confirm(
				__(
					'Are you sure you want to delete this event?',
					'nemtly-booking'
				)
			)
		) {
			return;
		}

		try {
			await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }`,
				method: 'DELETE',
			} );

			setNotices( [
				{
					type: 'success',
					message: __(
						'Event deleted successfully.',
						'nemtly-booking'
					),
				},
			] );

			if ( events.length === 1 && currentPage > 1 ) {
				setCurrentPage( currentPage - 1 );
			} else {
				await loadEvents( currentPage, itemsPerPage );
			}
		} catch ( err ) {
			setNotices( [
				{
					type: 'error',
					message:
						err.message ||
						__( 'Failed to delete event.', 'nemtly-booking' ),
				},
			] );
		}
	};

	/**
	 * Refresh events list
	 *
	 * @since PBOOKING_SINCE
	 */
	const handleRefresh = () => {
		loadEvents( currentPage, itemsPerPage );
	};

	const noticeColorMap = {
		success: {
			wrap: 'nb-bg-success-50 nb-border-success-500',
			text: 'nb-text-success-700',
			icon: (
				<svg
					className="nb-w-4 nb-h-4 nb-shrink-0"
					fill="none"
					viewBox="0 0 16 16"
				>
					<circle
						cx="8"
						cy="8"
						r="7"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<path
						d="M5 8l2 2 4-4"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
		},
		error: {
			wrap: 'nb-bg-danger-50 nb-border-danger-500',
			text: 'nb-text-danger-700',
			icon: (
				<svg
					className="nb-w-4 nb-h-4 nb-shrink-0"
					fill="none"
					viewBox="0 0 16 16"
				>
					<circle
						cx="8"
						cy="8"
						r="7"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<path
						d="M8 5v3.5M8 10.5v.5"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
			),
		},
		warning: {
			wrap: 'nb-bg-warning-50 nb-border-warning-500',
			text: 'nb-text-warning-700',
			icon: (
				<svg
					className="nb-w-4 nb-h-4 nb-shrink-0"
					fill="none"
					viewBox="0 0 16 16"
				>
					<path
						d="M8 2L14.5 13H1.5L8 2z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinejoin="round"
					/>
					<path
						d="M8 6.5v3M8 11v.5"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
			),
		},
	};

	const paginationProps =
		totalPages > 1
			? {
					currentPage,
					totalPages,
					totalItems,
					itemsPerPage,
					onPageChange: handlePageChange,
					onItemsPerPageChange: handleItemsPerPageChange,
			  }
			: null;

	return (
		<div className="nb-event-list nb-flex nb-flex-col nb-flex-1">
			{ /* Notices */ }
			{ notices.map( ( notice, index ) => {
				const style =
					noticeColorMap[ notice.type ] || noticeColorMap.warning;
				return (
					<div
						key={ index }
						className={ `nb-flex nb-items-start nb-gap-3 nb-px-4 nb-py-3 nb-rounded-lg nb-border nb-text-sm ${ style.wrap } ${ style.text }` }
					>
						{ style.icon }
						<span className="nb-flex-1">{ notice.message }</span>
						<button
							type="button"
							className="nb-shrink-0 nb-opacity-60 hover:nb-opacity-100 nb-transition-opacity"
							onClick={ () =>
								setNotices( ( prev ) =>
									prev.filter( ( _, i ) => i !== index )
								)
							}
							aria-label={ __( 'Dismiss', 'nemtly-booking' ) }
						>
							<svg
								className="nb-w-4 nb-h-4"
								fill="none"
								viewBox="0 0 16 16"
							>
								<path
									d="M4 4l8 8M12 4l-8 8"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
							</svg>
						</button>
					</div>
				);
			} ) }

			{ /* Content */ }
			<div className="nb-flex nb-flex-col nb-flex-1">
				<EventListTable
					events={ events }
					onDeleteEvent={ handleDeleteEvent }
					onEditEvent={ onEditEvent }
					onRefresh={ handleRefresh }
					onCreateNew={ onCreateNew }
					pagination={ paginationProps }
					isLoading={ isLoading }
					sort={ sort }
					onSortChange={ handleSortChange }
					hasActiveFilters={ hasActiveFilters }
					toolbar={
						<EventFilters
							searchValue={ searchInput }
							onSearchChange={ handleSearchChange }
							status={ status }
							onStatusChange={ handleStatusChange }
							eventType={ eventType }
							onEventTypeChange={ handleEventTypeChange }
							onClear={ handleClearFilters }
							hasActiveFilters={ hasActiveFilters }
						/>
					}
				/>
			</div>
		</div>
	);
};

export default EventList;
